import React, { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../interceptors";
import EmptyState from "../../components/shared/EmptyState";
import LoadingState from "../../components/shared/LoadingState";
import ErrorState from "../../components/shared/ErrorState";
import { toast } from "../../components/shared/toast";

// ── /candidate/practice ────────────────────────────────────────────────────
// Backend: POST /job-posting/api/candidate-portal/practice/list returns
// PracticeSessionDTO[] — the candidate's past practice runs with AI
// scoring + feedback + tips. /practice/run starts a new session but
// expects a candidate answer in the body, which means it needs the
// recording UI before it can be wired here. The hardcoded prompt list
// stays as suggestions; "Try it" toasts "coming soon" until the
// recording flow lands.
//
// Two sections:
//   1. Your practice history    — backend-driven list
//   2. Suggested questions      — curated prompts; click → coming-soon toast

const STARTER_PROMPTS = [
  { title: "Tell me about yourself",                                 difficulty: "Easy",   time: "2 min", category: "Behavioral" },
  { title: "Describe a challenging project you owned",                difficulty: "Medium", time: "3 min", category: "Behavioral" },
  { title: "What are your strengths and growth areas?",               difficulty: "Easy",   time: "2 min", category: "Behavioral" },
  { title: "Where do you see yourself in 3 years?",                   difficulty: "Easy",   time: "2 min", category: "Behavioral" },
  { title: "Explain a technical concept to a non-technical person",   difficulty: "Medium", time: "3 min", category: "Communication" },
  { title: "How do you handle tight deadlines?",                      difficulty: "Medium", time: "2 min", category: "Situational" },
];

const DIFFICULTY_TONE = {
  Easy:   "bg-emerald-100 text-emerald-800",
  Medium: "bg-amber-100 text-amber-800",
  Hard:   "bg-red-100 text-red-800",
};

function scoreTone(n) {
  if (n == null) return "text-vf-muted";
  if (n >= 8 || n >= 80) return "text-emerald-700 font-semibold";
  if (n >= 6 || n >= 60) return "text-amber-700";
  return "text-red-700";
}

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(); } catch { return ""; }
}

export default function CandidatePractice() {
  const candidateId = useMemo(() => {
    const raw = localStorage.getItem("candidateId") || localStorage.getItem("userId");
    return raw ? Number(raw) : null;
  }, []);

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(() => {
    if (!candidateId) {
      setLoading(false);
      setError("Not signed in as a candidate");
      return;
    }
    setLoading(true);
    setError(null);
    setErrorStatus(null);
    axiosInstance
      .post("/job-posting/api/candidate-portal/practice/list", { candidateId })
      .then((r) => {
        if (r.data?.success) {
          setSessions(Array.isArray(r.data.data) ? r.data.data : []);
        } else {
          setError(r.data?.message || "Couldn't load practice history");
          setErrorStatus(500);
        }
      })
      .catch((e) => {
        setError(e?.message || "Couldn't load practice history");
        setErrorStatus(e?.status || e?.response?.status || 500);
      })
      .finally(() => setLoading(false));
  }, [candidateId]);

  useEffect(() => {
    document.title = "Practice - Ample Hire";
    load();
  }, [load]);

  const practiceComingSoon = (title) => {
    toast.info("Practice recording is coming soon", {
      description: `When it lands, "${title}" will be one click to record and AI-score.`,
    });
  };

  return (
    <div className="max-w-3xl mx-auto space-y-5">
      <header>
        <h1 className="text-2xl font-semibold text-vf-base m-0">Practice interviews</h1>
        <p className="text-sm text-vf-muted mt-1">
          Warm up before the real one. Record yourself, see your score, get coaching.
        </p>
      </header>

      <aside
        aria-label="Practice tip"
        className="rounded-card bg-vf-tint border border-vf-highlight p-4 flex items-start gap-3"
      >
        <span className="text-xl flex-shrink-0" aria-hidden="true">🎯</span>
        <div>
          <div className="text-sm font-semibold text-vf-base">Practice is private</div>
          <p className="text-xs text-vf-muted mt-1 m-0 leading-relaxed">
            Practice sessions are never shared with recruiters. They're for you — review
            timing, body language, and clarity without pressure.
          </p>
        </div>
      </aside>

      {/* Past practice history — backend-driven */}
      <section aria-label="Your practice history" className="space-y-2.5">
        <div className="flex items-baseline justify-between">
          <h2 className="text-base font-semibold text-vf-base m-0">Your practice history</h2>
          {!loading && !error && sessions.length > 0 && (
            <span className="text-2xs text-vf-muted">{sessions.length} session{sessions.length === 1 ? "" : "s"}</span>
          )}
        </div>

        {loading ? (
          <div className="bg-white border border-subtle rounded-card p-4">
            <LoadingState variant="skeleton" shape="row" count={3} />
          </div>
        ) : error ? (
          <ErrorState
            status={errorStatus}
            description={errorStatus === 403 ? undefined : error}
            retry={load}
          />
        ) : sessions.length === 0 ? (
          <div className="bg-white border border-subtle rounded-card">
            <EmptyState
              icon={<span aria-hidden="true">🎤</span>}
              title="No practice runs yet"
              description="Pick a question below to get started once practice recording is live."
              compact
            />
          </div>
        ) : (
          <ul className="list-none p-0 m-0 space-y-2">
            {sessions.map((s) => {
              const open = expandedId === s.id;
              return (
                <li key={s.id} className="rounded-card border border-subtle bg-white">
                  <button
                    type="button"
                    onClick={() => setExpandedId(open ? null : s.id)}
                    aria-expanded={open}
                    aria-controls={`practice-${s.id}-detail`}
                    className="w-full text-left px-4 py-3 flex items-center gap-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-vf-primary"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-vf-base truncate">
                        {s.questionText || "Practice session"}
                      </div>
                      <div className="text-2xs text-vf-muted mt-0.5">
                        {s.category && <>{s.category} · </>}
                        {fmtDate(s.createdAt)}
                      </div>
                    </div>
                    <div className={`text-base font-mono ${scoreTone(s.aiScore)}`}>
                      {s.aiScore == null ? "—" : s.aiScore}
                    </div>
                    <span aria-hidden="true" className="text-vf-muted">{open ? "▾" : "▸"}</span>
                  </button>
                  {open && (
                    <div id={`practice-${s.id}-detail`} className="px-4 pb-4 pt-1 border-t border-subtle space-y-3 text-xs">
                      {s.candidateAnswer && (
                        <Block label="Your answer">{s.candidateAnswer}</Block>
                      )}
                      {s.aiFeedback && (
                        <Block label="AI feedback">{s.aiFeedback}</Block>
                      )}
                      {Array.isArray(s.aiTips) && s.aiTips.length > 0 && (
                        <Block label="Coaching tips">
                          <ul className="list-disc list-inside m-0 space-y-1">
                            {s.aiTips.map((t, i) => <li key={i}>{t}</li>)}
                          </ul>
                        </Block>
                      )}
                      {s.aiModelAnswer && (
                        <Block label="Model answer for reference">{s.aiModelAnswer}</Block>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Starter prompts — curated */}
      <section aria-label="Try these questions" className="space-y-2.5">
        <h2 className="text-base font-semibold text-vf-base m-0">Try these questions</h2>
        <ul className="list-none p-0 m-0 space-y-2">
          {STARTER_PROMPTS.map((q, i) => (
            <li
              key={i}
              className="rounded-card border border-subtle bg-white px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2.5 hover:border-vf-primary/30 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-vf-base">{q.title}</div>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  <span className="px-2 py-0.5 rounded-full text-2xs bg-vf-tint text-vf-base">{q.category}</span>
                  <span className={`px-2 py-0.5 rounded-full text-2xs font-semibold ${DIFFICULTY_TONE[q.difficulty] || "bg-vf-tint text-vf-base"}`}>
                    {q.difficulty}
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-2xs bg-vf-tint text-vf-muted">⏱ {q.time}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => practiceComingSoon(q.title)}
                className="text-xs px-3 py-1.5 rounded border border-subtle text-vf-base hover:bg-vf-tint focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vf-primary"
              >
                Try it →
              </button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function Block({ label, children }) {
  return (
    <div>
      <div className="text-2xs uppercase tracking-wide text-vf-muted font-semibold mb-1">{label}</div>
      <div className="text-vf-base whitespace-pre-line leading-relaxed">{children}</div>
    </div>
  );
}
